/**
 * Prominent CTA after createQuizDocument — open the interactive Quiz tab.
 */

import { ClipboardList, Play } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { testIds } from '@/lib/test-ids'
import type { QuizDocumentCreatedData } from '@/components/layout/chat-pane.types'

type QuizCreatedCardProps = {
  quiz: QuizDocumentCreatedData
  onOpen?: (documentId: string, event?: React.MouseEvent) => void | Promise<void>
  className?: string
}

export function QuizCreatedCard({ quiz, onOpen, className }: QuizCreatedCardProps) {
  const title = (quiz.title || 'Quiz').trim() || 'Quiz'
  const metaBits: string[] = []
  if (typeof quiz.questionCount === 'number' && quiz.questionCount > 0) {
    metaBits.push(`${quiz.questionCount} question${quiz.questionCount === 1 ? '' : 's'}`)
  }
  if (quiz.slug) metaBits.push(quiz.slug)
  if (quiz.filePath) metaBits.push(quiz.filePath)

  const open = async (e?: React.MouseEvent) => {
    if (!quiz.documentId) return
    window.dispatchEvent(
      new CustomEvent('document-created', {
        detail: {
          documentId: quiz.documentId,
          filePath: quiz.filePath,
          title: quiz.title,
        },
      }),
    )
    window.dispatchEvent(
      new CustomEvent('document-updated', {
        detail: { documentId: quiz.documentId },
      }),
    )
    if (onOpen) {
      await onOpen(quiz.documentId, e)
      return
    }
    window.dispatchEvent(
      new CustomEvent('open-document', {
        detail: { documentId: quiz.documentId },
      }),
    )
  }

  return (
    <div
      className={cn(
        'mt-3 rounded-lg border border-primary/25 bg-primary/5 p-4 shadow-sm',
        className,
      )}
      data-testid={testIds.chatPane.quizCreatedCard}
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
          <ClipboardList className="h-5 w-5" aria-hidden />
        </div>
        <div className="min-w-0 flex-1 space-y-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-primary/80">
              Quiz ready
            </p>
            <p className="text-sm font-semibold text-foreground truncate" title={title}>
              {title}
            </p>
            {metaBits.length > 0 ? (
              <p className="mt-0.5 text-xs text-muted-foreground truncate" title={metaBits.join(' · ')}>
                {metaBits.join(' · ')}
              </p>
            ) : null}
          </div>
          <Button
            type="button"
            size="sm"
            className="h-9 gap-2 font-medium"
            data-testid={testIds.chatPane.quizCreatedOpen}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              void open(e)
            }}
          >
            <Play className="h-4 w-4" aria-hidden />
            Open quiz
          </Button>
        </div>
      </div>
    </div>
  )
}
